/* eslint-disable camelcase */
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { Tag } from 'primereact/tag';
import React, { useEffect, useState } from 'react';
import { useGetScorecardFilterQuery } from '../../../components/scoreCardFilter/scoreCardFilterSlice';
import { useGetPrimaryApprovalWindowQuery } from '../../../pages/compensation/compensationSlice';
import { IHighlightedDates } from '../../../utils/types';
import './approvalCalendar.scss';

export const ApprovalCalendar = () => {
    const [inputDate, setDate] = useState(new Date());
    const [inputMonth, setMonth] = useState(new Date().getMonth() + 1);
    const [inputYear, setYear] = useState(new Date().getFullYear());
    const [scorecardDisable, setScorecardDisable] = useState(true);
    const [selectedScorecard, setSelectedScorecard] = useState(0);
    const [dates, setDates] = useState<IHighlightedDates[]>([]);
    const [minDate, setMinDate] = useState(new Date());
    const [maxDate, setMaxDate] = useState(new Date());
    const [approvalWindowStatusName, setApprovalWindowStatusName] = useState('');
    const [approvalWindowStatusPaused, setApprovalWindowStatusPaused] = useState(false);

    const { data: scorecardFilterData } = useGetScorecardFilterQuery({
        year: inputYear,
        month: inputMonth,
    }, { skip: !inputYear || !inputMonth });
    
    const { data: primaryApprovalWindowData, isFetching: isApprovalWindowFetching } = useGetPrimaryApprovalWindowQuery(
        {
            year: inputYear,
            month: inputMonth,
            scorecardVersionId: selectedScorecard,
            approvalWindowConfigLevelId: 3,
        },
        { skip: !inputYear || !inputMonth || !selectedScorecard }
    );

    useEffect(() => {
        if (scorecardFilterData?.data.length > 0) {
            setScorecardDisable(false);
            setSelectedScorecard(scorecardFilterData?.data[0].scorecard_version_id);
        } else {
            setScorecardDisable(true);
            setSelectedScorecard(0);
        }
    }, [scorecardFilterData]);
    const DateOnChange = (e: any) => {
        setDate(e.value);
        setMonth(e.value.getMonth() + 1);
        setYear(e.value.getFullYear());
        setDates([]);
    }
    const scorecardOnChange = (e: any) => {
        setSelectedScorecard(e.value);
        setDates([]);
    }
    const dateRanges = [
        { startKey: 'associate_start_date', endKey: 'associate_end_date', color: '#3299FF', label: 'Associate Approval Windows' },
        { startKey: 'team_lead_start_date', endKey: 'team_lead_end_date', color: '#A9A5FF', label: 'Team Lead Approval Windows' },
        { startKey: 'manager_start_date', endKey: 'manager_end_date', color: '#00D6BB', label: 'Manager Approval Windows'},
        { startKey: 'comp_team_start_date', endKey: 'comp_team_end_date', color: '#64D96E', label: 'Compensation Approval Windows'},
        { startKey: 'payout_transmit_date', endKey: 'payout_transmit_date', color: '#E23636', label: 'Payout Transmit Date'},
    ];
    useEffect(() => {
        if (!isApprovalWindowFetching && primaryApprovalWindowData?.data.length > 0) {
            const windowData = primaryApprovalWindowData?.data[0];
            if (windowData) {
                dateRanges.forEach(({ startKey, endKey, color }) => {
                    const startDate = windowData[startKey];
                    const endDate = windowData[endKey];
                    if (startDate && endDate) {
                        setDates(prevDates => [
                            ...prevDates,
                            {
                                startDate: new Date(startDate),
                                endDate: new Date(endDate),
                                color,
                            },
                        ]);
                    }
                });
                setApprovalWindowStatusName(windowData.approval_window_status_name);
                setApprovalWindowStatusPaused(windowData.approvals_paused);
                setMinDate(new Date(windowData.associate_start_date));
                setMaxDate(new Date(windowData.payout_transmit_date));
            }
        }
    }, [primaryApprovalWindowData]);
    const dateTemplate = (value: any) => {
        const { day, month, year } = value;
        const highlight = new Date(year, month, day);
        const isWithinRange = highlight >= minDate && highlight <= maxDate;
        if (highlight && isWithinRange) {
            let foundColor;
            const highlightInRange = dates.some(({ startDate, endDate, color }) => {
                if (startDate !== null && endDate !== null) {
                    foundColor = color;
                    return highlight >= startDate && highlight <= endDate;
                }
                return false;
            });
            if (highlightInRange) {
                return (
                    <span style={{
                        display: 'block',
                        padding: '1.5em',
                        borderRadius: '50%',
                        background: isWithinRange ? foundColor : 'transparent',
                        color: isWithinRange ? '#fff' : 'inherit',
                    }}>
                        {day}
                    </span>
                );
            }
        }
        return <span>{day}</span>;
    };

    return (
        <div>
            <div className="row ms-3 mt-1">
                <div className="col-md-4">
                    <h6 style={{ fontWeight: '600', fontSize: '16px' }}>Month</h6>
                    <Calendar
                        id='inputCal'
                        value={inputDate}
                        onChange={DateOnChange}
                        view="month"
                        showIcon
                        className="custom-calendar me-4"
                        dateFormat="MM, yy"
                    />
                </div>
                <div className="col-md-4">
                    <h6 style={{ fontWeight: '600', fontSize: '16px' }}>Scorecard</h6>
                    <Dropdown
                        id='scorecardDropdown'
                        disabled={scorecardDisable}
                        value={selectedScorecard}
                        options={scorecardFilterData?.data}
                        onChange={scorecardOnChange}
                        optionLabel="scorecard_name"
                        optionValue={'scorecard_version_id'}
                        placeholder={'No Scorecards'}
                        className="custom-dropdown w-200"
                    />
                </div>
                <div className="col-md-4 mt-2">
                    {primaryApprovalWindowData?.data.length > 0 && (
                        approvalWindowStatusPaused ? (
                            <Tag
                                className="mr-2 mt-4"
                                icon="pi pi-times"
                                severity="danger"
                                value={approvalWindowStatusName + ' (Stalled)'}
                                rounded
                            />
                        ) : (
                            <Tag
                                className="mr-2 mt-4"
                                icon="pi pi-info-circle"
                                severity="info"
                                value={approvalWindowStatusName}
                                rounded
                            />
                        )
                    )}
                </div>
            </div>
            {
                dates.length > 0 ? (
                    <div>
                        <div className="row ms-3 mt-2">
                            {dateRanges.slice(0, 3).map((dot, index) => (
                                <div className="col-md-4" key= {index}>
                                    <div className="color-dot" key={index}>
                                        <div className="dot" style={{ backgroundColor: dot.color }} />
                                        <span style={{ fontSize: '13px' }}>{dot.label}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="row ms-3 mt-1">
                            {dateRanges.slice(3).map((dot, index) => (
                                <div className="col-md-5 ms-5" key={index}>
                                    <div className="color-dot ms-3" key={index}>
                                        <div className="dot" style={{ backgroundColor: dot.color }} />
                                        <span style={{ fontSize: '13px' }}>{dot.label}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="row ms-2 mt-2">
                            <div className="col-md-12 text-center">
                                <div className="card flex justify-content-center" style={{ display: 'inline-block' }}>
                                    <Calendar
                                        id='approvalCal'
                                        inline
                                        value={minDate}
                                        maxDate={maxDate}
                                        minDate={minDate}
                                        dateTemplate={dateTemplate}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    ''
                )
            }
        </div >
    );
};